// Account info component for the account page.

// Imports ----------------------------------------------------------------------------------------------------------

import Container from '@components/atoms/container/accountInfoContainer';
import DeviceConfig from '@devices/config';
import Item from '@components/elements/account/accountItem';
import Package from '../../../../package.json';
import TranslationManager from '@shared/assets/locale/translationsManager';
import User from '@shared/classes/user';
import { useMemo } from 'react';
import { useAppSelector } from '@shared/store/hooks';
import { userSelector } from '@shared/store/slices/user.slice';

// Constants ----------------------------------------------------------------------------------------------------------

const LITERALS = 'PAGES.SETTINGS.ACCOUNT';

/**
 * Account info component for the account page.
 * Displays the account information in a list format.
 *
 * @returns {JSX.Element} Account info component
 */
export default function AccountInfo() {
  // Hooks
  const { info } = useAccountInfo();

  // Render
  return (
    <Container id={'accountInfo'}>
      {Object.values(info).map((item) => (
        <Item key={item.title} {...item} />
      ))}
    </Container>
  );
}

/**
 * Hook to use the account info.
 * Handles the account info logic and returns the props for the account info.
 *
 * @returns {Object} Object with the props for the account info
 */
function useAccountInfo() {
  // Selectors
  const { email, mobile, name, operator, planId, postalCode, userId } =
    useAppSelector(userSelector);

  // Variables
  const plan = User.getPlan(planId || null);

  // Memoized values
  const info = useMemo(() => {
    return {
      name: {
        title: TranslationManager.get(`${LITERALS}.DATA.NAME`),
        value: name,
      },
      user: {
        title: TranslationManager.get(`${LITERALS}.DATA.USER_ID`),
        value: userId,
      },
      operator: {
        title: TranslationManager.get(`${LITERALS}.DATA.OPERATOR`),
        value: operator?.name,
      },
      plan: {
        title: TranslationManager.get(`${LITERALS}.DATA.SERVICES`),
        value: plan?.name,
      },
      postalCode: {
        title: TranslationManager.get(`${LITERALS}.DATA.POSTAL_CODE`),
        value: postalCode,
      },
      email: {
        title: TranslationManager.get(`${LITERALS}.DATA.EMAIL`),
        value: email,
      },
      mobile: {
        title: TranslationManager.get(`${LITERALS}.DATA.MOBILE`),
        value: mobile,
      },
      model: {
        title: TranslationManager.get(`${LITERALS}.DATA.DEVICE_MODEL`),
        value: DeviceConfig?.info.model,
      },
      appVersion: {
        title: TranslationManager.get(`${LITERALS}.DATA.APP_VERSION`),
        value:
          Package.launcherVersion +
          '/' +
          Package.version +
          (location.href.includes('stage') ||
          location.href.includes('localhost')
            ? `.${Package.rc}`
            : ''),
      },
      os: {
        title: TranslationManager.get(`${LITERALS}.DATA.SO`),
        value: DeviceConfig?.info.os + ' ' + DeviceConfig?.info.version,
      },
    };
  }, [name, userId, operator, plan, postalCode, email, mobile]);

  // Return values
  return { info };
}
